import { type ReactNode } from "react";
import { useAuth, useConfig, type Role } from "./provider";
import { NoAccessPage, SignInScreen, useSigninCopy } from "./SignInPage";

export function RequireAuth({ children, fallback }: { children: ReactNode; fallback?: ReactNode }) {
  const auth = useAuth();
  if (auth.isLoading) return <p className="muted">Signing you in…</p>;
  if (!auth.isAuthenticated) return <>{fallback ?? <SignInScreen />}</>;
  return <>{children}</>;
}

export { SignInScreen as SignIn };

/** Signed in, but this account has no agent at all: the sign-in page's own "not set up yet" state. */
export function NotSetUp() {
  const auth = useAuth();
  const config = useConfig();
  const copy = useSigninCopy(config.api_base);
  return <NoAccessPage copy={copy} config={config} auth={auth} />;
}

export function NotEntitled({ what }: { what: string }) {
  const auth = useAuth();
  if (!auth.user?.agents.length) return <NotSetUp />;
  return (
    <div className="center">
      <h2>You don&apos;t have access to {what}</h2>
      <p>Contact your administrator to be given access.</p>
    </div>
  );
}

export function RequireAgent({ id, children }: { id: string; children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.agents.includes(id)) return <NotEntitled what={`the ${id} agent`} />;
  return <>{children}</>;
}

export function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user || !user.roles.some((r) => roles.includes(r))) return <NotEntitled what="this page" />;
  return <>{children}</>;
}

export const hasRole = (user: { roles: string[] } | null, ...roles: string[]) => !!user && user.roles.some((r) => roles.includes(r));
